import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Tabs, Tab, Stack,
  Chip, Divider, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Link, TextField,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { HRApiWrapper } from 'api/HRApiWrapper';
import ProfileDrawer from './ProfileDrawer';
import ResizableDrawer from 'components/Layout/ResizableDrawer';

interface Props {
  open: boolean;
  onClose: () => void;
  profile: any;
  hrApi: HRApiWrapper;
  skills: any[];
  skillLevels: any[];
  trainingCourses: any[];
  certifications: any[];
  roles: any[];
  seniorities: any[];
}

const ProfileDetail: React.FC<Props> = ({
  open, onClose, profile, hrApi,
  skills, skillLevels, trainingCourses, certifications, roles, seniorities,
}) => {
  const toast = useToastMessageQueue();
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [cvUrl, setCvUrl] = useState<string | null>(null);
  const [generatingCv, setGeneratingCv] = useState(false);
  const [errors] = useState({});

  const [localProfile, setLocalProfile] = useState<any>(profile);
  const [addSkillId, setAddSkillId] = useState<number | ''>('');
  const [addSkillLevelId, setAddSkillLevelId] = useState<number | ''>('');
  const [addCourseId, setAddCourseId] = useState<number | ''>('');
  const [addCertId, setAddCertId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [editingCourse, setEditingCourse] = useState<{ id: number; idCourse: number; progress: number; endDate: string } | null>(null);
  const [editingSkill, setEditingSkill] = useState<{ id: number; idSkill: number } | null>(null);
  const [editingCert, setEditingCert] = useState<{ id: number; idCert: number; certificationDate: string; certificationDue: string; score: string; attempts: string; url: string } | null>(null);

  const reloadProfile = useCallback(async () => {
    try {
      const res = await hrApi.getProfileById('1', profile.id);
      setLocalProfile(res.data?.data ?? res.data);
    } catch (error) {
      handleAPIError(error, toast, errors);
    }
  }, [profile.id]);

  useEffect(() => { setLocalProfile(profile); }, [profile]);

  useEffect(() => {
    if (open && profile?.id) { reloadProfile(); }
  }, [open, profile?.id, reloadProfile]);

  const linkedSkillIds = new Set((localProfile.profileSkills ?? []).filter((s: any) => !s.isDeleted).map((s: any) => s.idSkill ?? s.skill?.id));
  const linkedCourseIds = new Set((localProfile.profileTrainingCourses ?? []).filter((s: any) => !s.isDeleted).map((s: any) => s.idTrainingCourse ?? s.trainingCourse?.id));
  const linkedCertIds = new Set((localProfile.profileTrainingCertifications ?? []).filter((s: any) => !s.isDeleted).map((s: any) => s.idTrainingCertification ?? s.trainingCertification?.id));

  const handleGenerateCv = async () => {
    setGeneratingCv(true);
    setCvUrl(null);
    try {
      const res = await hrApi.generateCv('1', localProfile.id);
      setCvUrl((res.data as any)?.data ?? null);
      toast.success({ body: intl.get('hr.profile.cvGenerated') });
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setGeneratingCv(false);
    }
  };

  // ─── Skills tab ────────────────────────────────────────────────────────────

  const skillColumns: GridColDef[] = [
    { field: 'skill', headerName: intl.get('hr.skill.name'), flex: 1, valueGetter: (_: any, row: any) => row.skill?.name ?? '' },
    { field: 'skillLevel', headerName: intl.get('hr.skill.level.name'), width: 160, valueGetter: (_: any, row: any) => row.skillLevel?.name ?? '' },
    {
      field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => {
            setEditingSkill({ id: params.row.id, idSkill: params.row.idSkill ?? params.row.skill?.id });
            setAddSkillId(params.row.idSkill ?? params.row.skill?.id ?? '');
            setAddSkillLevelId(params.row.idSkillLevel ?? params.row.skillLevel?.id ?? '');
          }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => hrApi.removeSkillFromProfile('1', params.row.id).then(reloadProfile)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const courseColumns: GridColDef[] = [
    { field: 'trainingCourse', headerName: intl.get('hr.course.name'), flex: 1, valueGetter: (_: any, row: any) => row.trainingCourse?.name ?? '' },
    { field: 'progress', headerName: intl.get('hr.profile.course.progress'), width: 110, renderCell: (p) => `${p.value ?? 0}%` },
    {
      field: 'endDate', headerName: intl.get('hr.profile.course.completed'), width: 130,
      valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '—',
    },
    {
      field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => {
            const idCourse = params.row.idTrainingCourse ?? params.row.trainingCourse?.id;
            setEditingCourse({ id: params.row.id, idCourse, progress: params.row.progress ?? 0, endDate: params.row.endDate ? params.row.endDate.split('T')[0] : '' });
            setAddCourseId(idCourse ?? '');
          }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => hrApi.removeTrainingCourseFromProfile('1', params.row.id).then(reloadProfile)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const certColumns: GridColDef[] = [
    { field: 'trainingCertification', headerName: intl.get('hr.certification.name'), flex: 1, valueGetter: (_: any, row: any) => row.trainingCertification?.name ?? '' },
    { field: 'certificationDate', headerName: intl.get('hr.profile.cert.date'), width: 130, valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '' },
    { field: 'certificationDue', headerName: intl.get('hr.profile.cert.expires'), width: 130, valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '' },
    { field: 'score', headerName: intl.get('hr.profile.cert.score'), width: 100 },
    {
      field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => {
            const idCert = params.row.idTrainingCertification ?? params.row.trainingCertification?.id;
            setEditingCert({
              id: params.row.id, idCert,
              certificationDate: params.row.certificationDate ? params.row.certificationDate.split('T')[0] : '',
              certificationDue: params.row.certificationDue ? params.row.certificationDue.split('T')[0] : '',
              score: params.row.score?.toString() ?? '',
              attempts: params.row.attempts?.toString() ?? '',
              url: params.row.url ?? '',
            });
            setAddCertId(idCert ?? '');
          }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => hrApi.removeCertificationFromProfile('1', params.row.id).then(reloadProfile)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <ResizableDrawer open={open} onClose={onClose} defaultWidth={680}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography variant="h6">{localProfile.role?.name}</Typography>
            <Typography variant="body2" color="text.secondary">{localProfile.seniority?.name}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setEditOpen(true)}>{intl.get('common.edit') || 'Edit'}</Button>
            <Button
              size="small" variant="outlined" startIcon={generatingCv ? <CircularProgress size={14} /> : undefined}
              onClick={handleGenerateCv} disabled={generatingCv}
            >
              {intl.get('hr.profile.generateCV')}
            </Button>
            {cvUrl && (
              <Link href={cvUrl} target="_blank" rel="noreferrer" underline="none">
                <Button size="small" variant="contained" startIcon={<DownloadIcon />}>
                  {intl.get('hr.profile.downloadCV')}
                </Button>
              </Link>
            )}
            <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={onClose}>
              {intl.get('common.done') || 'Done'}
            </Button>
          </Stack>
        </Box>

        {localProfile.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{localProfile.description}</Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={intl.get('hr.profile.tab.skills')} />
          <Tab label={intl.get('hr.profile.tab.courses')} />
          <Tab label={intl.get('hr.profile.tab.certifications')} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Box style={{ width: '100%', height: 300 }}>
              <DataGrid
                rows={(localProfile.profileSkills ?? []).filter((s: any) => !s.isDeleted)}
                columns={skillColumns}
                hideFooter
                disableRowSelectionOnClick
              />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" mt={2}>
              <FormControl size="small" sx={{ flex: 1 }} disabled={!!editingSkill}>
                <InputLabel>{intl.get('hr.skill.name')}</InputLabel>
                <Select value={addSkillId} onChange={(e) => setAddSkillId(e.target.value as number)} label={intl.get('hr.skill.name')}>
                  {editingSkill
                    ? skills.filter((s) => s.id === editingSkill.idSkill).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)
                    : skills.filter((s) => !linkedSkillIds.has(s.id)).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)
                  }
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>{intl.get('hr.skill.level.name')}</InputLabel>
                <Select value={addSkillLevelId} onChange={(e) => setAddSkillLevelId(e.target.value as number)} label={intl.get('hr.skill.level.name')}>
                  {skillLevels.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="outlined" disabled={!addSkillId || !addSkillLevelId || saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    if (editingSkill) {
                      await hrApi.updateProfileSkill('1', { id: editingSkill.id, idSkillLevel: Number(addSkillLevelId) });
                      setEditingSkill(null);
                    } else {
                      await hrApi.addSkillToProfile('1', { idProfile: localProfile.id, idSkill: Number(addSkillId), idSkillLevel: Number(addSkillLevelId) });
                    }
                    setAddSkillId(''); setAddSkillLevelId('');
                    await reloadProfile();
                  } catch (error) { handleAPIError(error, toast, errors); }
                  finally { setSaving(false); }
                }}>
                {editingSkill ? intl.get('common.save') : intl.get('common.add')}
              </Button>
              {editingSkill && (
                <Button size="small" onClick={() => { setEditingSkill(null); setAddSkillId(''); setAddSkillLevelId(''); }}>
                  {intl.get('common.cancel')}
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box style={{ width: '100%', height: 300 }}>
              <DataGrid
                rows={(localProfile.profileTrainingCourses ?? []).filter((s: any) => !s.isDeleted)}
                columns={courseColumns}
                hideFooter
                disableRowSelectionOnClick
              />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1} mt={2}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 180 }} disabled={!!editingCourse}>
                <InputLabel>{intl.get('hr.course.name')}</InputLabel>
                <Select value={addCourseId} onChange={(e) => setAddCourseId(e.target.value as number)} label={intl.get('hr.course.name')}>
                  {editingCourse
                    ? trainingCourses.filter((c) => c.id === editingCourse.idCourse).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)
                    : trainingCourses.filter((c) => !linkedCourseIds.has(c.id)).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)
                  }
                </Select>
              </FormControl>
              {editingCourse && (
                <>
                  <TextField size="small" type="number" label={intl.get('hr.profile.course.progress.label') || 'Progress (%)'}
                    value={editingCourse.progress} inputProps={{ min: 0, max: 100 }} sx={{ width: 140 }}
                    onChange={(e) => setEditingCourse((c) => c && ({ ...c, progress: Number(e.target.value) }))} />
                  <TextField size="small" type="date" label={intl.get('hr.profile.course.endDate.label') || 'Completion Date'}
                    value={editingCourse.endDate} InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
                    onChange={(e) => setEditingCourse((c) => c && ({ ...c, endDate: e.target.value }))} />
                </>
              )}
              <Button variant="outlined" disabled={!addCourseId || saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    if (editingCourse) {
                      await hrApi.updateTrainingCourseProgress('1', { id: editingCourse.id, progress: editingCourse.progress, endDate: editingCourse.endDate || null });
                      setEditingCourse(null);
                    } else {
                      await hrApi.assignTrainingCourse('1', { idProfile: localProfile.id, idTrainingCourse: Number(addCourseId) });
                    }
                    setAddCourseId('');
                    await reloadProfile();
                  } catch (error) { handleAPIError(error, toast, errors); }
                  finally { setSaving(false); }
                }}>
                {editingCourse ? intl.get('common.save') : intl.get('common.add')}
              </Button>
              {editingCourse && (
                <Button size="small" onClick={() => { setEditingCourse(null); setAddCourseId(''); }}>
                  {intl.get('common.cancel')}
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {tab === 2 && (
          <Box>
            <Box style={{ width: '100%', height: 300 }}>
              <DataGrid
                rows={(localProfile.profileTrainingCertifications ?? []).filter((s: any) => !s.isDeleted)}
                columns={certColumns}
                hideFooter
                disableRowSelectionOnClick
              />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1} mt={2}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 180 }} disabled={!!editingCert}>
                <InputLabel>{intl.get('hr.certification.name')}</InputLabel>
                <Select value={addCertId} onChange={(e) => setAddCertId(e.target.value as number)} label={intl.get('hr.certification.name')}>
                  {editingCert
                    ? certifications.filter((c) => c.id === editingCert.idCert).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)
                    : certifications.filter((c) => !linkedCertIds.has(c.id)).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)
                  }
                </Select>
              </FormControl>
              {editingCert && (
                <>
                  <TextField size="small" type="date" label={intl.get('hr.profile.cert.date')} value={editingCert.certificationDate}
                    InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
                    onChange={(e) => setEditingCert((c) => c && ({ ...c, certificationDate: e.target.value }))} />
                  <TextField size="small" type="date" label={intl.get('hr.profile.cert.expires')} value={editingCert.certificationDue}
                    InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
                    onChange={(e) => setEditingCert((c) => c && ({ ...c, certificationDue: e.target.value }))} />
                  <TextField size="small" type="number" label={intl.get('hr.profile.cert.score')} value={editingCert.score}
                    sx={{ width: 100 }}
                    onChange={(e) => setEditingCert((c) => c && ({ ...c, score: e.target.value }))} />
                  <TextField size="small" type="number" label={intl.get('hr.profile.cert.attempts')} value={editingCert.attempts}
                    sx={{ width: 100 }}
                    onChange={(e) => setEditingCert((c) => c && ({ ...c, attempts: e.target.value }))} />
                  <TextField size="small" label="URL" value={editingCert.url}
                    sx={{ width: 200 }}
                    onChange={(e) => setEditingCert((c) => c && ({ ...c, url: e.target.value }))} />
                </>
              )}
              <Button variant="outlined" disabled={!addCertId || saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    if (editingCert) {
                      await hrApi.updateCertification('1', {
                        id: editingCert.id,
                        certificationDate: editingCert.certificationDate || null,
                        certificationDue: editingCert.certificationDue || null,
                        score: editingCert.score ? Number(editingCert.score) : null,
                        attempts: editingCert.attempts ? Number(editingCert.attempts) : null,
                        url: editingCert.url || null,
                      });
                      setEditingCert(null);
                    } else {
                      await hrApi.assignCertification('1', { idProfile: localProfile.id, idTrainingCertification: Number(addCertId), certificationDate: new Date().toISOString() });
                    }
                    setAddCertId('');
                    await reloadProfile();
                  } catch (error) { handleAPIError(error, toast, errors); }
                  finally { setSaving(false); }
                }}>
                {editingCert ? intl.get('common.save') : intl.get('common.add')}
              </Button>
              {editingCert && (
                <Button size="small" onClick={() => { setEditingCert(null); setAddCertId(''); }}>
                  {intl.get('common.cancel')}
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </ResizableDrawer>

      <ProfileDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={async (data) => { await hrApi.updateProfile('1', { ...data, id: localProfile.id }); setEditOpen(false); await reloadProfile(); onClose(); }}
        item={localProfile}
        roles={roles}
        seniorities={seniorities}
      />
    </>
  );
};

export default ProfileDetail;
